import { useState } from "react";
import { type HiddenCols, type TableHeader } from "./tableTypes";

export const useDraggableTableHeaders = (
    tableHeaders: TableHeader[],
    setTableHeaders: (
        value: TableHeader[]
    ) => void
) => {
    const initDragInfos = {
        draggedItem: {},
        draggedFromIndex: 0,
        draggedToIndex: 0,
    };

    const [dragInfos, setDragInfos] = useState(initDragInfos);
    const reorderTableHeaders = () => {
        const { draggedFromIndex, draggedToIndex } = dragInfos;

        const newHeaderArray: TableHeader[] = new Array(0);
        if (draggedFromIndex < draggedToIndex) {
            tableHeaders.map((item: TableHeader, index: number) => {
                if (index < draggedFromIndex) {
                    return newHeaderArray.push(item);
                }
                if (draggedFromIndex <= index && index < draggedToIndex) {
                    return newHeaderArray.push(tableHeaders[index + 1]);
                }
                if (index === draggedToIndex) {
                    return newHeaderArray.push(tableHeaders[draggedFromIndex]);
                }
                if (index > draggedToIndex) {
                    return newHeaderArray.push(tableHeaders[index]);
                }
                return;
            });
        } else if (draggedFromIndex === draggedToIndex) {
            tableHeaders.map((item: TableHeader, index: number) => {
                return newHeaderArray.push(tableHeaders[index]);
            });
        } else if (draggedToIndex < draggedFromIndex) {
            tableHeaders.map((item: TableHeader, index: number) => {
                if (index < draggedToIndex) {
                    return newHeaderArray.push(item);
                }
                if (index === draggedToIndex) {
                    return newHeaderArray.push(tableHeaders[draggedFromIndex]);
                }
                if (draggedToIndex < index && index <= draggedFromIndex) {
                    return newHeaderArray.push(tableHeaders[index - 1]);
                }
                if (draggedFromIndex < index) {
                    return newHeaderArray.push(tableHeaders[index]);
                }
            });
        }
        setTableHeaders(newHeaderArray);
    };

    const handleDragStart = (
        e: React.DragEvent<HTMLTableCellElement>,
        currentItemId: string
    ) => {
        const currentItemIndex = tableHeaders.findIndex(
            (item) => item.id === currentItemId
        );

        setDragInfos({
            ...dragInfos,
            draggedItem: tableHeaders[currentItemIndex],
            draggedFromIndex: currentItemIndex,
        });

        document.addEventListener("dragover", (e) => {
            e.preventDefault();
        });
    };

    const handleDragEnter = (
        e: React.DragEvent<HTMLTableCellElement>,
        enterItemId: string
    ) => {
        const enterItemIndex = tableHeaders.findIndex(
            (item) => item.id === enterItemId
        );

        setDragInfos({
            ...dragInfos,
            draggedToIndex: enterItemIndex,
        });
    };

    const handleDragEnd = () => {
        reorderTableHeaders();
        setDragInfos(initDragInfos);
    };

    return {
        handleDragStart,
        handleDragEnter,
        handleDragEnd,
    };
};

export const showTableColumns = <AllowedIds,>(
    initialHeaders: HiddenCols<AllowedIds>[],
    columnIds: AllowedIds[]
) => {
    /** Use original initial cols,
     * we only want to show extra cols on top of the default few
     * to avoid complication.
     */
    return initialHeaders.reduce(
        (acc: HiddenCols<AllowedIds>[], curr) => {
            if (columnIds.includes(curr.id)) {
                return [
                    ...acc,
                    {
                        id: curr.id,
                        hidden: false,
                    },
                ];
            } else {
                return [...acc, curr];
            }
        },
        []
    );
};

export const hideTableColumn = <AllowedIds,>(
    hiddenCols: HiddenCols<AllowedIds>[],
    columnId: AllowedIds
) => {
    const index = hiddenCols.findIndex((item) => item.id === columnId);
    const newCols = [
        ...hiddenCols.slice(0, index),
        {
            id: columnId,
            hidden: true,
        },
        ...hiddenCols.slice(index + 1, hiddenCols.length),
    ];

    return newCols;
};
